'use client'

import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@/src/icons'
import { cva } from 'class-variance-authority'

import { cn } from '@/registry/reapit/lib/utils'
import { Flex, Stack } from '@/registry/reapit/ui/flex'
import { Grid } from '@/registry/reapit/ui/grid'
import { Text, TextCaption } from '@/registry/reapit/ui/text'

/*
 * At a glance — DS component (Phase 6)
 *
 * DS Page: 8403087
 * https://design.reapit.com.au/1a7e83c3d/p/674311-at-a-glance
 *
 * A compound component for surfacing summary stat cards from a larger dataset.
 * Cards can be static (display-only) or interactive (filter / navigation).
 *
 * Compound API:
 *   <AtAGlance>
 *     <AtAGlance.Header>
 *       <AtAGlance.Title>At a glance</AtAGlance.Title>
 *       <AtAGlance.Action><Button size="small">Configure</Button></AtAGlance.Action>
 *     </AtAGlance.Header>
 *     <AtAGlance.List layout="grid" cols={4} gap={4}>
 *       <AtAGlance.Card layout="vertical">
 *         <AtAGlance.Icon><Building2Icon /></AtAGlance.Icon>
 *         <AtAGlance.Value>1,284</AtAGlance.Value>
 *         <AtAGlance.Label>Total properties</AtAGlance.Label>
 *         <AtAGlance.Description>+12 this week</AtAGlance.Description>
 *       </AtAGlance.Card>
 *     </AtAGlance.List>
 *   </AtAGlance>
 */

// ─── Card CVA ────────────────────────────────────────────────────────────────

const cardVariants = cva(
  [
    // Base — structure, background, border
    'rounded-lg border border-border bg-card text-card-foreground',
    // Transition for interactive states
    'transition-colors',
  ],
  {
    variants: {
      layout: {
        vertical: 'p-4',
        compact: 'p-3',
        horizontal: 'p-4',
      },
      interactive: {
        true: 'cursor-pointer focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring',
        false: '',
      },
      selected: {
        true: 'border-2 border-border-action bg-fill-action-lightest',
        false: '',
      },
    },
    compoundVariants: [
      // Hover only when interactive and NOT selected
      {
        interactive: true,
        selected: false,
        className: 'hover:bg-fill-subtle hover:border-border-neutral-light-darker',
      },
    ],
    defaultVariants: {
      layout: 'vertical',
      interactive: false,
      selected: false,
    },
  },
)

// ─── Prop types ───────────────────────────────────────────────────────────────

type AtAGlanceRootProps = React.HTMLAttributes<HTMLElement> & {
  /** Render as a different element. Defaults to 'section'. */
  as?: React.ElementType
}

type AtAGlanceHeaderProps = React.HTMLAttributes<HTMLDivElement>

type AtAGlanceTitleProps = React.HTMLAttributes<HTMLHeadingElement> & {
  /** Render as a different heading element. Defaults to 'h2'. */
  as?: React.ElementType
}

type AtAGlanceActionProps = React.HTMLAttributes<HTMLDivElement>

type AtAGlanceListLayout = 'grid' | 'carousel'
type AtAGlanceListGap = 2 | 3 | 4 | 6 | 8
type AtAGlanceListCols = 1 | 2 | 3 | 4 | 5 | 6

type AtAGlanceListProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Display mode. Default: 'grid'. */
  layout?: AtAGlanceListLayout
  /** Gap between cards (Tailwind scale). Default: 4. */
  gap?: AtAGlanceListGap
  /**
   * Number of grid columns (base / mobile-first).
   * Only used when layout="grid". Default: 1.
   */
  cols?: AtAGlanceListCols
  /** Responsive column count at DS Tablet (sm ≥ 768px). */
  smCols?: AtAGlanceListCols
  /** Responsive column count at DS Desktop (md ≥ 1024px). */
  mdCols?: AtAGlanceListCols
  /** Responsive column count at DS WideScreen (lg ≥ 1440px). */
  lgCols?: AtAGlanceListCols
  /**
   * Fixed card width for carousel layout.
   * Accepts a CSS value string ('240px', '15rem') or a number (px).
   * Default: 240.
   */
  cardWidth?: number | string
}

type AtAGlanceCardLayout = 'vertical' | 'compact' | 'horizontal'

type AtAGlanceCardProps = Omit<React.HTMLAttributes<HTMLElement>, 'onClick'> & {
  /** Card content layout. Default: 'vertical'. */
  layout?: AtAGlanceCardLayout
  /**
   * When true, renders the card as a <button> and applies
   * hover / focus / selected interactive states.
   */
  interactive?: boolean
  /**
   * Persistent selected state — use for single-select filter behaviour.
   * Only meaningful when interactive is true.
   */
  selected?: boolean
  /** Click handler. Setting this also implies interactive=true. */
  onClick?: () => void
}

type AtAGlanceIconProps = React.HTMLAttributes<HTMLSpanElement> & {
  /**
   * When true wraps the icon in the DS action fill pill (for horizontal cards).
   * Defaults to false — pass pill to get the coloured rounded background.
   */
  pill?: boolean
}

type AtAGlanceValueProps = React.HTMLAttributes<HTMLSpanElement> & {
  /**
   * Override the default text size.
   * Vertical and horizontal cards default to text-2xl;
   * compact cards default to text-xl.
   */
  size?: 'xl' | '2xl'
}

type AtAGlanceLabelProps = React.HTMLAttributes<HTMLSpanElement>

type AtAGlanceDescriptionProps = React.HTMLAttributes<HTMLSpanElement>

type AtAGlanceHorizontalBodyProps = React.HTMLAttributes<HTMLDivElement>

// ─── Root ─────────────────────────────────────────────────────────────────────

/**
 * Root wrapper for the At a glance compound component.
 * Provides no layout of its own — wraps Header + List as a logical unit.
 */
function AtAGlanceRoot({ as: Comp = 'section', className, children, ...props }: AtAGlanceRootProps) {
  return (
    <Comp data-slot="at-a-glance" className={cn('w-full', className)} {...props}>
      {children}
    </Comp>
  )
}

// ─── Header ──────────────────────────────────────────────────────────────────

/**
 * Optional header row — renders a title (left) and an optional action (right).
 * Wrap a title string in <AtAGlance.Title> and an action element in <AtAGlance.Action>.
 */
function Header({ className, children, ...props }: AtAGlanceHeaderProps) {
  return (
    <Flex between align="center" className={cn('mb-4', className)} data-slot="at-a-glance-header" {...props}>
      {children}
    </Flex>
  )
}

// ─── Title ───────────────────────────────────────────────────────────────────

/**
 * Section title for the At a glance block.
 * DS: "DS L" (text-lg / 20px, semi-bold).
 */
function Title({ as: Comp = 'h2', className, children, ...props }: AtAGlanceTitleProps) {
  return (
    <Comp
      data-slot="at-a-glance-title"
      className={cn('text-lg font-semibold leading-7 text-foreground', className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── Action ──────────────────────────────────────────────────────────────────

/**
 * Right-aligned action slot in the header — typically a small Button or icon-button.
 */
function Action({ className, children, ...props }: AtAGlanceActionProps) {
  return (
    <div data-slot="at-a-glance-action" className={cn('shrink-0', className)} {...props}>
      {children}
    </div>
  )
}

// ─── List ─────────────────────────────────────────────────────────────────────

/**
 * Container for a set of At a glance cards.
 * Renders as a responsive Grid (grid layout) or horizontally scrolling
 * carousel with edge gradient masks and hover-reveal scroll buttons.
 */
function List({
  layout = 'grid',
  gap = 4,
  cols,
  smCols,
  mdCols,
  lgCols,
  cardWidth = 240,
  className,
  children,
  ...props
}: AtAGlanceListProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)

  // Calculate card width string for carousel
  const cardWidthCss = typeof cardWidth === 'number' ? `${cardWidth}px` : cardWidth

  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1)
  }, [])

  React.useEffect(() => {
    if (layout !== 'carousel') return
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    // Also observe resize so buttons update if window resizes
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      ro.disconnect()
    }
  }, [layout, updateScrollState])

  const scrollBy = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    const amount = typeof cardWidth === 'number' ? cardWidth : parseInt(cardWidthCss, 10) || 240
    el.scrollBy({ left: direction === 'right' ? amount : -amount, behavior: 'smooth' })
  }

  if (layout === 'carousel') {
    // Compute gap pixel value from the Tailwind gap scale (4px units)
    const gapPx = gap * 4

    return (
      <div data-slot="at-a-glance-list" data-layout="carousel" className={cn('group relative', className)} {...props}>
        {/* Left scroll button — visible on hover when scrolled right */}
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => scrollBy('left')}
          className={cn(
            'absolute left-0 top-1/2 z-10 -translate-y-1/2 -translate-x-1/2',
            'flex size-8 items-center justify-center rounded-full',
            'bg-card border border-border shadow-sm',
            'opacity-0 transition-opacity duration-150',
            'group-hover:opacity-100',
            !canScrollLeft && 'pointer-events-none !opacity-0',
          )}
        >
          <ChevronLeftIcon className="size-4 text-foreground" aria-hidden="true" />
        </button>

        {/* Scroll track */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scrollbar-none"
          style={{
            gap: `${gapPx}px`,
            // Dynamic edge gradient masks based on scroll position
            maskImage:
              [
                canScrollLeft ? 'linear-gradient(to right, transparent 0, black 48px)' : '',
                canScrollRight ? 'linear-gradient(to left, transparent 0, black 48px)' : '',
              ]
                .filter(Boolean)
                .join(', ') || 'none',
            WebkitMaskImage:
              [
                canScrollLeft ? 'linear-gradient(to right, transparent 0, black 48px)' : '',
                canScrollRight ? 'linear-gradient(to left, transparent 0, black 48px)' : '',
              ]
                .filter(Boolean)
                .join(', ') || 'none',
          }}
        >
          {React.Children.map(children, (child) => (
            <div style={{ minWidth: cardWidthCss, maxWidth: cardWidthCss, flexShrink: 0 }}>{child}</div>
          ))}
        </div>

        {/* Right scroll button */}
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => scrollBy('right')}
          className={cn(
            'absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/2',
            'flex size-8 items-center justify-center rounded-full',
            'bg-card border border-border shadow-sm',
            'opacity-0 transition-opacity duration-150',
            'group-hover:opacity-100',
            !canScrollRight && 'pointer-events-none !opacity-0',
          )}
        >
          <ChevronRightIcon className="size-4 text-foreground" aria-hidden="true" />
        </button>
      </div>
    )
  }

  // Grid layout — use the Grid layout primitive
  return (
    <Grid
      data-slot="at-a-glance-list"
      data-layout="grid"
      cols={cols ?? 1}
      smCols={smCols ?? (cols ? undefined : 2)}
      mdCols={mdCols}
      lgCols={lgCols ?? (cols ? undefined : 4)}
      gap={gap}
      className={className}
      {...(props as React.ComponentProps<'div'>)}
    >
      {children}
    </Grid>
  )
}

// ─── Card ─────────────────────────────────────────────────────────────────────

/**
 * Individual summary card. Supports vertical, compact, and horizontal layouts.
 * Pass `interactive` (or `onClick`) for filter / navigation behaviour.
 * Pass `selected` for persistent selected state (single-selection per DS spec).
 */
function Card({
  layout = 'vertical',
  interactive,
  selected = false,
  onClick,
  className,
  children,
  ...props
}: AtAGlanceCardProps) {
  const isInteractive = interactive ?? !!onClick

  const sharedClassName = cn(cardVariants({ layout, interactive: isInteractive, selected }), className)

  if (isInteractive) {
    return (
      <button
        type="button"
        data-slot="at-a-glance-card"
        data-layout={layout}
        data-selected={selected || undefined}
        onClick={onClick}
        className={cn(sharedClassName, 'w-full text-left')}
        aria-pressed={selected}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        <CardInner layout={layout}>{children}</CardInner>
      </button>
    )
  }

  return (
    <div
      data-slot="at-a-glance-card"
      data-layout={layout}
      className={sharedClassName}
      {...(props as React.HTMLAttributes<HTMLDivElement>)}
    >
      <CardInner layout={layout}>{children}</CardInner>
    </div>
  )
}

// Internal layout wrapper — avoids duplicating layout logic in the two render branches
function CardInner({ layout, children }: { layout: AtAGlanceCardLayout; children: React.ReactNode }) {
  if (layout === 'horizontal') {
    return (
      <Flex align="start" gap={3}>
        {children}
      </Flex>
    )
  }
  // vertical and compact — stack children
  return <Stack gap={1}>{children}</Stack>
}

// ─── Icon ─────────────────────────────────────────────────────────────────────

/**
 * Icon slot for an At a glance card.
 * Pass `pill` to wrap the icon in the DS action-tinted pill (for horizontal cards).
 * Without `pill`, the icon renders at natural size with action colour.
 */
function Icon({ pill, className, children, ...props }: AtAGlanceIconProps) {
  if (pill) {
    return (
      <span
        data-slot="at-a-glance-icon"
        className={cn(
          'flex shrink-0 size-10 items-center justify-center rounded-lg bg-fill-action-lightest text-icon-action',
          className,
        )}
        {...props}
      >
        {children}
      </span>
    )
  }

  return (
    <span
      data-slot="at-a-glance-icon"
      className={cn('flex shrink-0 size-5 items-center justify-center text-icon-action', className)}
      {...props}
    >
      {children}
    </span>
  )
}

// ─── Value ────────────────────────────────────────────────────────────────────

/**
 * The primary metric value. Renders in primary colour (purple) at DS XL or 2XL scale.
 * Default size is 2xl; pass size="xl" for compact cards.
 */
function Value({ size, className, children, ...props }: AtAGlanceValueProps) {
  const sizeClass = size === 'xl' ? 'text-xl' : 'text-2xl'

  return (
    <span
      data-slot="at-a-glance-value"
      className={cn('block font-semibold leading-tight text-primary', sizeClass, className)}
      {...props}
    >
      {children}
    </span>
  )
}

// ─── Label ────────────────────────────────────────────────────────────────────

/**
 * Short descriptor for the metric. DS S / 14px, regular weight.
 */
function Label({ className, children, ...props }: AtAGlanceLabelProps) {
  return (
    <Text
      as="span"
      size="sm"
      colour="default"
      data-slot="at-a-glance-label"
      className={cn('block', className)}
      {...props}
    >
      {children}
    </Text>
  )
}

// ─── Description ─────────────────────────────────────────────────────────────

/**
 * Optional supplementary hint beneath the label. DS XS / 12px, muted foreground.
 */
function Description({ className, children, ...props }: AtAGlanceDescriptionProps) {
  return (
    <TextCaption data-slot="at-a-glance-description" className={cn('block', className)} {...props}>
      {children}
    </TextCaption>
  )
}

// ─── HorizontalBody ──────────────────────────────────────────────────────────

/**
 * Convenience wrapper for the right-hand text block in a horizontal card.
 * Stacks Value, Label, and optional Description in a compact column.
 */
function HorizontalBody({ className, children, ...props }: AtAGlanceHorizontalBodyProps) {
  return (
    <Stack gap={0} className={cn('min-w-0 flex-1', className)} data-slot="at-a-glance-horizontal-body" {...props}>
      {children}
    </Stack>
  )
}

// ─── Compound assembly ────────────────────────────────────────────────────────

const AtAGlance = Object.assign(AtAGlanceRoot, {
  Header,
  Title,
  Action,
  List,
  Card,
  Icon,
  Value,
  Label,
  Description,
  HorizontalBody,
})

// ─── Namespace — prop types for consumer use ──────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace AtAGlance {
  export type Props = AtAGlanceRootProps
  export namespace Header {
    export type Props = AtAGlanceHeaderProps
  }
  export namespace Title {
    export type Props = AtAGlanceTitleProps
  }
  export namespace Action {
    export type Props = AtAGlanceActionProps
  }
  export namespace List {
    export type Layout = AtAGlanceListLayout
    export type Gap = AtAGlanceListGap
    export type Cols = AtAGlanceListCols
    export type Props = AtAGlanceListProps
  }
  export namespace Card {
    export type Layout = AtAGlanceCardLayout
    export type Props = AtAGlanceCardProps
  }
  export namespace Icon {
    export type Props = AtAGlanceIconProps
  }
  export namespace Value {
    export type Props = AtAGlanceValueProps
  }
  export namespace Label {
    export type Props = AtAGlanceLabelProps
  }
  export namespace Description {
    export type Props = AtAGlanceDescriptionProps
  }
  export namespace HorizontalBody {
    export type Props = AtAGlanceHorizontalBodyProps
  }
}

export { AtAGlance }
